import React, { useState, useEffect } from 'react';
import { format, parseISO } from 'date-fns';
import { useForecastCalenderAPI } from 'src/API/rest.forecast_calender';
import { Input } from 'src/shadcn/ui/input';
import type { ForecastCalenderListResponse } from 'src/API/rest.forecast_calender';
import { useGetLatestCalendarDate } from 'src/API/rest.forecast_calender';

export default function StatusReport() {
  const [selectedDate, setSelectedDate] = useState<string>('');
  const latestdate = useGetLatestCalendarDate({ trigger: true });

  // Set initial date only if selectedDate is empty
  useEffect(() => {
    if (!selectedDate && latestdate && latestdate.latestCalendarDate && latestdate.latestCalendarDate.date) {
      const latest = latestdate.latestCalendarDate.date;
      if (latest) {
        const parsedDate = parseISO(latest);
        setSelectedDate(format(parsedDate, 'yyyy-MM'));
      }
    }
  }, [latestdate]); // Remove setSelectedDate from dependencies to avoid unnecessary triggers

  // Use the API hook
  const {
    isLoading,
    isError,
    forcastCalendersList: data,
    refetchforcastCalendersList,
    error,
  } = useForecastCalenderAPI({
    date: selectedDate ? `${selectedDate}-01` : '', // Format as YYYY-MM-DD for API
    retryCount: 3,
    trigger: !!selectedDate, // Only trigger API if selectedDate is set
  });

  // Handle date change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = e.target.value;
    if (newDate) {
      setSelectedDate(newDate);
    }
  };

  // Format date for display
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  // Get status color and text - based on FT07_is_completed from CalenderLines
  const getStatusInfo = (isCompleted: boolean | undefined, status?: number | string) => {
    if (isCompleted === true || status === 1) {
      return { color: 'text-green-600 dark:text-green-200', bgColor: 'bg-green-200 dark:bg-green-900', text: 'Finished', bordercolor: 'border-green-200'  };
    }
    return { color: 'text-red-600 dark:text-red-200', bgColor: 'bg-red-200 dark:bg-red-900', text: 'Not Finished',bordercolor: 'border-red-200'  };
  };

  if (isLoading) {
    return (
      <div className="grow flex flex-col overflow-auto items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading forecast calendar...</p>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="grow flex flex-col overflow-auto items-center justify-center min-h-[400px]">
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400 mb-4">
            Error loading data: {error?.message || 'Unknown error'}
          </p>
          <button
            onClick={() => refetchforcastCalendersList()}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="grow flex flex-col items-center">
      {/* Header with Date Picker */}
      <div className="w-full max-w-6xl mb-4 ">
        <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
          <h1 className="text-xl font-semibold text-gray-800 dark:text-gray-200">
            Forecast Calendar Status Report
          </h1>
          <div className="flex items-center gap-3">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Select Month:
            </label>
            <Input
              type="month"
              className="w-48"
              onChange={handleChange}
              value={selectedDate}
              min="2024-01"
              max={format(new Date(), 'yyyy-MM')}
            />
          </div>
        </div>
      </div>

      <div className="grow flex flex-col overflow-scroll bg-white dark:bg-vstargray-900 rounded-md w-full max-w-6xl mx-4 text-center">
        {data && data.length > 0 ? (
          data.map((item: ForecastCalenderListResponse) => {
            const line = item.CalenderLines[0];
            const statusInfo = getStatusInfo(line?.FT07_is_completed);

            const getRespStatus = (resp: any) => {
              const isCompleted = resp.FT08_is_completed !== undefined
                ? resp.FT08_is_completed
                : resp.isFinished;
              const completedDate = resp.FT08_completed_date !== undefined
                ? resp.FT08_completed_date
                : resp.finishedDate;
              return { isCompleted, completedDate };
            };

            return (
              <div
                key={item.ForecastCalenderMasterID}
                className="border-b border-gray-200 dark:border-gray-700 p-1 pl-3 flex items-center gap-4"
              >
                <div className="flex flex-col flex-1 min-w-56 max-w-96 sticky top-1 self-start">
                  <div className={`bg-gray-100  dark:bg-vstargray-800 rounded-lg p-3  shadow-sm border ${statusInfo.bordercolor} `}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-start font-semibold text-gray-700 dark:text-gray-200">
                        {item.SINo}. {item.Action}
                      </span>
                      <span
                        className={`text-xs font-medium px-2 py-0.5 rounded ${statusInfo.bgColor} ${statusInfo.color}`}
                      >
                        {statusInfo.text}
                      </span>
                    </div>
                    <div className="flex flex-col gap-1 text-start text-xs text-gray-600 dark:text-gray-400">
                      <span>
                        {line?.TargetDate
                          ? `Target: ${formatDate(line.TargetDate)}`
                          : 'No Target Date'}
                      </span>
                      {line?.FT07_completed_date && (
                        <span>
                          Finished: {formatDate(line.FT07_completed_date)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-3 overflow-x-auto p-2">
                  {line?.Responsibility && line.Responsibility.length > 0 ? (
                  // Split employees into columns of max 4 per column
                  Array.from({ length: Math.ceil(line.Responsibility.length / 4) }).map((_, colIdx) => (
                    <div key={colIdx} className="flex flex-col gap-2 min-w-[160px]">
                    {line.Responsibility.slice(colIdx * 4, colIdx * 4 + 4).map((resp: any) => {
                      const respStatus = getRespStatus(resp);
                      return (
                      <div
                        key={resp.ForecastCalenderResponsibilityID}
                        className="bg-white dark:bg-vstargray-800 border hover:shadow-lg   border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 shadow-sm flex items-center gap-3"
                      >
                        <div
                        className={`w-3 h-3 rounded-full ${respStatus.isCompleted ? 'bg-green-500' : 'bg-red-500'}`}
                        aria-hidden
                        />
                        <div className="text-left">
                        <div className="text-sm font-medium text-gray-800 dark:text-gray-200">
                          {resp.Employee?.Name || 'Unknown'}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          {respStatus.completedDate ? `Finished: ${formatDate(respStatus.completedDate)}` : 'Not finished'}
                        </div>
                        </div>
                      </div>
                      );
                    })}
                    </div>
                  ))
                  ) : (
                  <div className="text-xs text-gray-500 dark:text-gray-400 px-3 py-1">No employees assigned</div>
                  )}
                </div>
              </div>
            );
          })
        ) : (
          <div className="p-12 text-center text-gray-500 dark:text-gray-400">
            <div className="mb-4">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <p className="text-lg font-medium mb-2">No forecast calendar data</p>
            <p className="mb-4">
              No activities found for {selectedDate ? format(new Date(selectedDate), 'MMMM yyyy') : 'selected month'}
            </p>
            <button
              onClick={() => refetchforcastCalendersList()}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md transition-colors"
            >
              Refresh Data
            </button>
          </div>
        )}
      </div>

      {data && data.length > 0 && (
        <div className="w-full max-w-4xl mt-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-md mx-4">
          <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400">
            <span>Total Activities: {data.length}</span>
            <span className="flex items-center gap-2">
              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
              Completed: {data.filter(item => item.CalenderLines[0]?.FT07_is_completed).length}/{data.length}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}